import { ArrayUtil, Collection, JSUtil, ModelRow, StringUtil } from "@mcleod/core";
import { Component } from "./Component";
import { ComponentProps } from "./ComponentProps";
import {
    ContainerContentsChangedEvent, ContainerContentsChangedListener
} from "../events/ContainerContentsChangedEvent";
import { DataSourceMode, Layout, serializeComponents } from "..";
import { DomEvent } from "../events/DomEvent";
import { ListenerListDef } from "./ListenerListDef";
import { Printable } from "./PrintableComponent";
import { ValidationResult } from "./ValidationResult";

export class Container extends Component {
    private static readonly contentsChangedListenerDef: ListenerListDef = { listName: "_contentsChangedListeners" };
    protected _components: Component[] = [];
    private allowContentsChangedListener: boolean = true;    

    constructor(type: string, props?: Partial<ComponentProps>) {
        super(type, props);
    }

    [Symbol.iterator]() {
        return ArrayUtil.arrayIterator(this.components);
    }

    public async ensureLoaded(): Promise<void> {
        await Promise.all(this.getComponentsToLoad().map(component => component.ensureLoaded()));
    }

    public override isEmpty(): boolean {
        return this.getComponentCount() == 0;
    }

    public getComponentCount(): number {
        return this._components?.length ?? 0;
    }

    public getComponent(index: number) {
        return this._components[index];
    }

    get components(): Component[] {
        return this._components;
    }

    set components(_value: Component[]) {
        throw new Error("The " + this.constructor.name + " class must implement a setter for its components.")
    }

    public get firstComponent(): Component {
        return ArrayUtil.getFirstElement(this.components);
    }

    public get lastComponent(): Component {
        return ArrayUtil.getLastElement(this.components);
    }

    getChildrenForCloning(): Component[] {
        return this.components;
    }

    add(_child: Component) {
        throw new Error("The " + this.constructor.name + " class must implement add().")
    }

    removeAt(_index: number, _domEvent?: DomEvent) {
        throw new Error("The " + this.constructor.name + " class must implement removeAt().")
    }

    remove(child: Component, errorIfNotAChild: boolean = false) {
        const index = this.indexOf(child);
        if (index < 0) {
            if (errorIfNotAChild !== false)
                throw new Error("The specified component is not a child of this container.");
        } else
            this.removeAt(index);
    }

    removeAll() {
        try {
            this.allowContentsChangedListener = false;
            const removedComponents = [ ...this.components ];
            for (let i = this.components.length - 1; i >= 0; i--) {
                this.removeAt(i);
            }
            this.allowContentsChangedListener = true;
            this.fireContentsChangedListener(null, removedComponents);
        } finally {
            this.allowContentsChangedListener = true;
        }
    }

    indexOf(child: Component) {
        return this._components.indexOf(child);
    }

    get printable(): boolean {
        return this["_mixin-Printable-printable"];
    }

    set printable(value: boolean) {
        if (this["printableValueMatches"](value) === true) {
            return;
        }
        this["_mixin-Printable-printable"] = value;
        const isThisPrintable = this.printable;
        if (this.components != null)
            for (const comp of this.components)
                if (comp["printable"] != null)
                    comp["printable"] = isThisPrintable;
    }

    override get managingComponent(): Component {
        return super.managingComponent;
    }

    override set managingComponent(value: Component) {
        super.managingComponent = value;
        for (const component of this.components) {
            component.managingComponent = this.managingComponent;
        }
    }

    public addListenersToAll(listeners: any) {
        if (listeners != null) {
            for (const component of this.components) {
                component.addListenersToAll(listeners);
            }
        }
    }

    getSearchValues(): string[] {
        const result = [];
        for (const component of this.components) {
            result.push(...component.getSearchValues());
        }
        return result;
    }

    childBreakChanged(child: Component) { // do nothing in a basic container
    }

    tieComponentsToOwner(components: Component[], owner: any) {
        for (const comp of components) {
            comp.owner = owner;
            if (comp.id != null)
                owner[comp.id] = comp;
            if (comp instanceof Container)
                this.tieComponentsToOwner(comp.components, owner);

            // Yuck - this is specific to Tabset.... make this more generic
            const compAsAny = comp as any;
            if (compAsAny.beforeTools != null)
                this.tieComponentsToOwner(compAsAny.beforeTools, owner);
            if (compAsAny.afterTools != null)
                this.tieComponentsToOwner(compAsAny.afterTools, owner);
        }
    }

    getRecursiveChildren(collectNestedLayoutChildren: boolean = true): Component[] {
        const result: Component[] = [];
        this._addRecursiveChildren(this, result, collectNestedLayoutChildren);
        return result;
    }

    getRecursiveChildrenById(collectNestedLayoutChildren: boolean = true) {
        const children = this.getRecursiveChildren(collectNestedLayoutChildren);
        return children.filter(child => !StringUtil.isEmptyString(child.id)).map(child => child.id);
    }

    private _addRecursiveChildren(container: Container, result: Component[], collectNestedLayoutChildren: boolean) {
        for (const comp of container) {
            result.push(comp);
            if (comp instanceof Container) {
                if (!collectNestedLayoutChildren && comp instanceof Layout && comp.isNested)
                    continue;
                comp._addRecursiveChildren(comp, result, collectNestedLayoutChildren);
            }
        }
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (data instanceof ModelRow && this.field != null && (data.get(this.field) instanceof ModelRow)) {
            data = data.get(this.field);
        }
        this.getInnerComponentsForDisplay()?.forEach(component => {
            component.displayData(data, allData, rowIndex);
        });
    }

    protected getInnerComponentsForDisplay(): Component[] {
        return this.components;
    }

    override validate(checkRequired: boolean, showErrors: boolean = true): ValidationResult[] {
        let result: ValidationResult[] = null;
        for (const comp of this.components) {
            const thisResult: ValidationResult[] = comp.validate(checkRequired, showErrors);
            if (thisResult !== null) {
                if (result == null)
                    result = thisResult;
                else
                    result = result.concat(thisResult);
            }
        }
        return result;
    }

    override resetValidation() {
        for (const comp of this.components)
            comp.resetValidation();
    }

    _designerDrop(component: Component) {
        this.add(component);
    }

    _serializeNonProps(): string {
        if (this.components != null && this.components.length > 0)
            return "\"components\": " + serializeComponents(this.components, null,) + ",\n";
        return "";
    }

    replace(component: Component, withComponent: Component) {
        const index = this.indexOf(component);
        if (index >= 0) {
            this.components[index] = withComponent;
            this.relayout();
            this.fireContentsChangedListener(withComponent, component);
        }
    }

    insert(component: Component, index: number): Component {
        this.components.splice(index, 0, component);
        this.relayout();
        this.fireContentsChangedListener(component, null);
        return component;
    }

    insertBefore(component: Component, existingComponent: Component): Component {
        let index = existingComponent != null ? this.indexOf(existingComponent) : -1;
        if (index < 0) {
            index = this.components.length;
        }
        return this.insert(component, index);
    }

    relayout() {
        try {
            this.allowContentsChangedListener = false;
            this.internalRelayout();
        } finally {
            this.allowContentsChangedListener = true;
        }
    }

    /**
     * Override this method in sub-classes to provide the logic of how that sub-class re-renders itself.  This method
     * exists so that we can control when the contents changed listener fires (see sets of the
     * allowContentsChangedListener variable).
     */
    protected internalRelayout() {
    }

    findFocusableComponent(): Component | undefined {
        for (const comp of this.components)
            if (comp.isFocusable())
                return comp;
        return undefined;
    }

    isFocusable() {
        return this.visible && this.findFocusableComponent() != null;
    }

    focus() {
        this.findFocusableComponent()?.focus();
    }

    public getFirstFocusableChild(): Component {
        for (const component of this?.components) {
            if (component.isFocusable())
                return component;
            if (component instanceof Container) {
                const focusableComp = component.getFirstFocusableChild();
                if (focusableComp != null)
                    return focusableComp;
            }
        }
    }

    public setPropOnChildren(propName: string, value: any, setOnlyIfExists: boolean = true) {
        for (const component of this.components) {
            if (component instanceof Container) {
                component.setPropOnChildren(propName, value, setOnlyIfExists);
            }
            else {
                if (setOnlyIfExists === false || (setOnlyIfExists === true && component[propName] != null)) {
                    component.setProp(value, propName, null);
                }
            }
        }
    }

    findComponentByType<T extends Component>(type: new (...args: any[]) => T): T | null {
        return this.findComponentsByType(type)[0] ?? null;
    }

    findComponentsByType<T extends Component>(type: new (...args: any[]) => T): T[] {
        return this.getRecursiveChildren().filter(c => c instanceof type) as T[];
    }

    public findComponentById(id: String | Function): Component {
        let result: Component;
        this.forEveryChildComponent(c => {
            if ((id instanceof Function && id(c)) || c.id == id)
                result = c;
        });
        return result;
    }

    public findComponentByField(field: string): Component[] {
        const result: Component[] = [];
        if (StringUtil.isEmptyString(field))
            return result;
        for (const component of this.components) {
            if (component.field === field)
                result.push(component);
            else if (component instanceof Container) {
                const innerComps = component.findComponentByField(field);
                for (const innerComp of innerComps) {
                    result.push(innerComp);
                }
            }
        }
        return result;
    }

    public forEveryChildComponent(callback: (component: Component) => void) {
        for (const component of this.components) {
            callback(component);
            if (component instanceof Container)
                component.forEveryChildComponent(callback);
        }
    }

    public forEveryChildComponentReverse(callback: (component: Component) => void) {
        for (let x = this.components.length - 1; x >= 0; x--) {
            const component = this.components[x];
            callback(component);
            if (component instanceof Container)
                component.forEveryChildComponentReverse(callback);
        }
    }

    public forEveryTopLevelChildComponent(callback: (component: Component) => void) {
        for (const component of this.components) {
            callback(component);
        }
    }

    public forEveryTopLevelChildComponentReverse(callback: (component: Component) => void) {
        for (let x = this.components.length - 1; x >= 0; x--) {
            callback(this.components[x]);
        }
    }

    override updateBoundData(row: ModelRow, mode: DataSourceMode) {
        for (const component of this.components) {
            component.updateBoundData(row, mode);
        }
    }

    growChildComponent(height: number) {
        if (this.components?.length !== 0)
            return;
        this._components[0].growToContainerHeight(height);
    }

    public override doBeforeComponentEnlarge(componentsToEnlarge: Component[]) {
        this["_tempRemovedComponents"] = [...this.components];
        this.removeAll();
        componentsToEnlarge.forEach((component: Component) => this.add(component));
    }

    public override doAfterComponentsShrink() {
        for (const removedComp of this["_tempRemovedComponents"]) {
            this.add(removedComp);
        }
        delete this["_tempRemovedComponents"];
    }

    public get enlarged(): boolean {
        return super.enlarged;
    }

    public set enlarged(value: boolean) {
        if (this.enlarged === value)
            return;
        super.enlarged = value;
        if (this.enlarged === false) {
            for (const component of this.components) {
                component.enlarged = value;
            }
        }
    }

    public override discoverIncludedComponents(): Component[] {
        return this.components;
    }

    protected getComponentsToLoad(): Component[] {
        return this.discoverIncludedComponents() ?? [];
    }

    public setLastComponentRowBreak(value: boolean) {
        const lastComponent = ArrayUtil.getLastElement(this.components);
        if (lastComponent != null)
            lastComponent.rowBreak = true;
    }

    public addContentsChangedListener(value: ContainerContentsChangedListener): Container {
        return this.addEventListener(Container.contentsChangedListenerDef, value) as Container;
    }

    public removeContentsChangedListener(value: ContainerContentsChangedListener): Container {
        return this.removeEventListener(Container.contentsChangedListenerDef, value) as Container;
    }

    protected fireContentsChangedListener(childrenAdded: Component|Component[], childrenRemoved: Component|Component[],
        domEvent?: DomEvent) {
        if (this.allowContentsChangedListener !== false) {
            const event = new ContainerContentsChangedEvent(this, childrenAdded, childrenRemoved, domEvent);
            this.fireListeners(Container.contentsChangedListenerDef, event);
        }
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "contentsChanged": { ...Container.contentsChangedListenerDef }
        };
    }
}

JSUtil.applyMixins(Container, [Printable]);
